'use client'

import * as React from 'react'
import useEmblaCarousel, {type UseEmblaCarouselType} from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import {CircleArrowLeft, CircleArrowRight} from 'lucide-react'

import {cn} from '@/lib/utils'
import {Button} from '@/components/ui/button'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
    opts?: CarouselOptions
    plugins?: CarouselPlugin
    orientation?: 'horizontal' | 'vertical'
    setApi?: (api: CarouselApi) => void
    autoplay?: boolean
    autoplayDelay?: number
    stopOnInteraction?: boolean
    stopOnMouseEnter?: boolean
}

type CarouselContextProps = {
    carouselRef: ReturnType<typeof useEmblaCarousel>[0]
    api: ReturnType<typeof useEmblaCarousel>[1]
    scrollPrev: () => void
    scrollNext: () => void
    canScrollPrev: boolean
    canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

export function useCarousel() {
    const context = React.useContext(CarouselContext)

    if (!context) {
        throw new Error('useCarousel must be used within a <Carousel />')
    }

    return context
}

type UseContentNavButtonType = {
    selectedIndex: number
    scrollSnaps: number[]
    onContentNavButtonClick: (index: number) => void
}

export const useContentNavButton = (
    api: ReturnType<typeof useEmblaCarousel>[1]
): UseContentNavButtonType => {
    const [selectedIndex, setSelectedIndex] = React.useState(0)
    const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([])

    const onContentNavButtonClick = React.useCallback(
        (index: number) => {
            if (!api) return
            api.scrollTo(index)
        },
        [api]
    )

    const onInit = React.useCallback((api: ReturnType<typeof useEmblaCarousel>[1]) => {
        if (!api) return
        setScrollSnaps(api.scrollSnapList())
    }, [])

    const onSelect = React.useCallback((api: ReturnType<typeof useEmblaCarousel>[1]) => {
        if (!api) return
        setSelectedIndex(api.selectedScrollSnap())
    }, [])

    React.useEffect(() => {
        if (!api) return

        onInit(api)
        onSelect(api)
        api.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect)
    }, [api, onInit, onSelect])

    return {
        selectedIndex,
        scrollSnaps,
        onContentNavButtonClick
    }
}

const Carousel = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & CarouselProps & {'data-testid'?: string | undefined}
>(
    (
        {
            orientation = 'horizontal',
            opts,
            setApi,
            plugins,
            className,
            children,
            'data-testid': dataTestId,
            autoplay = false,
            autoplayDelay = 4000,
            stopOnInteraction = true,
            stopOnMouseEnter = false,
            ...props
        },
        ref
    ) => {
        // yeah this is wierd 🙃  but The autoplay plugin is optional and controlled by the autoplay boolean prop. By conditionally pushing it to the
        // plugins array we can control whether or not it is used.
        const allPlugins = React.useMemo(() => {
            const pluginArray = plugins ? [...plugins] : []
            if (autoplay) {
                pluginArray.push(
                    Autoplay({
                        delay: autoplayDelay,
                        stopOnInteraction,
                        stopOnMouseEnter
                    })
                )
            }
            return pluginArray
        }, [plugins, autoplay, autoplayDelay, stopOnInteraction, stopOnMouseEnter])

        const [carouselRef, api] = useEmblaCarousel(
            {
                ...opts,
                axis: orientation === 'horizontal' ? 'x' : 'y'
            },
            allPlugins
        )
        const [canScrollPrev, setCanScrollPrev] = React.useState(false)
        const [canScrollNext, setCanScrollNext] = React.useState(false)

        const onSelect = React.useCallback((api: CarouselApi) => {
            if (!api) {
                return
            }

            setCanScrollPrev(api.canScrollPrev())
            setCanScrollNext(api.canScrollNext())
        }, [])

        const scrollPrev = React.useCallback(() => {
            api?.scrollPrev()
        }, [api])

        const scrollNext = React.useCallback(() => {
            api?.scrollNext()
        }, [api])

        const handleKeyDown = React.useCallback(
            (event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === 'ArrowLeft') {
                    event.preventDefault()
                    scrollPrev()
                } else if (event.key === 'ArrowRight') {
                    event.preventDefault()
                    scrollNext()
                }
            },
            [scrollPrev, scrollNext]
        )

        React.useEffect(() => {
            if (!api || !setApi) {
                return
            }

            setApi(api)
        }, [api, setApi])

        React.useEffect(() => {
            if (!api) {
                return
            }

            onSelect(api)
            api.on('reInit', onSelect)
            api.on('select', onSelect)

            return () => {
                api?.off('select', onSelect)
            }
        }, [api, onSelect])

        return (
            <CarouselContext.Provider
                value={{
                    carouselRef,
                    api: api,
                    opts,
                    orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
                    scrollPrev,
                    scrollNext,
                    canScrollPrev,
                    canScrollNext
                }}
            >
                <div
                    ref={ref}
                    onKeyDownCapture={handleKeyDown}
                    className={cn('carousel relative flex flex-col', className)}
                    role="region"
                    aria-roledescription="carousel"
                    data-testid={dataTestId || 'carousel'}
                    {...props}
                >
                    {children}
                </div>
            </CarouselContext.Provider>
        )
    }
)
Carousel.displayName = 'Carousel'

const CarouselContent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & {'data-testid'?: string | undefined}
>(({className, 'data-testid': dataTestId, ...props}, ref) => {
    const {carouselRef, orientation} = useCarousel()

    return (
        <div ref={carouselRef} className="overflow-hidden">
            <div
                ref={ref}
                className={cn(
                    'carousel-content',
                    'flex',
                    orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
                    className
                )}
                data-testid={dataTestId || 'carousel-Content'}
                {...props}
            />
        </div>
    )
})
CarouselContent.displayName = 'CarouselContent'

export const slidesVisibleValuesDesktop = ['1', '2', '3', '4', '5', '6']
export const slidesVisibleValuesMobile = ['1', '2', '3']

export type CarouselSlidesVisibleTypeDesktop =
    | '1'
    | '1.25'
    | '2'
    | '2.25'
    | '3'
    | '3.25'
    | '4'
    | '4.25'
    | '5'
    | '5.25'
    | '6'
export type CarouselSlidesVisibleTypeMobile = '1' | '1.25' | '2' | '2.25' | '3' | '3.25' | '4'
export type CarouselSlidePaddingType = '0' | '1' | '2' | '3' | '4' | '5' | '6'

type CarouselItemProps = {
    className?: string
    slidesVisible?: CarouselSlidesVisibleTypeDesktop
    showPeek?: boolean
    showPeekMobile?: boolean
    slidesVisibleMobile?: CarouselSlidesVisibleTypeMobile
    slidePadding?: CarouselSlidePaddingType
    slidePaddingMobile?: CarouselSlidePaddingType
    children: React.ReactNode
}

const flexBasisClasses = {
    '1': 'basis-full',
    '1.25': 'basis-[85%]',
    '2': 'basis-1/2',
    '2.25': 'basis-[45%]',
    '3': 'basis-1/3',
    '3.25': 'basis-[30%]',
    '4': 'basis-1/4'
}

const lgFlexBasisClasses = {
    '1': 'lg:basis-full',
    '1.25': 'lg:basis-[85%]',
    '2': 'lg:basis-1/2',
    '2.25': 'lg:basis-[45%]',
    '3': 'lg:basis-1/3',
    '3.25': 'lg:basis-[30%]',
    '4': 'lg:basis-1/4',
    '4.25': 'lg:basis-[23.5%]',
    '5': 'lg:basis-1/5',
    '5.25': 'lg:basis-[18.5%]',
    '6': 'lg:basis-1/6'
}

const paddingClasses = {
    '0': 'px-0',
    '1': 'px-1',
    '2': 'px-2',
    '3': 'px-3',
    '4': 'px-4',
    '5': 'px-5',
    '6': 'px-6'
}
const lgPaddingClasses = {
    '0': 'lg:px-0',
    '1': 'lg:px-1',
    '2': 'lg:px-2',
    '3': 'lg:px-3',
    '4': 'lg:px-4',
    '5': 'lg:px-5',
    '6': 'lg:px-6'
}

const CarouselItem = React.forwardRef<
    HTMLDivElement,
    CarouselItemProps & {'data-testid'?: string | undefined}
>(
    (
        {
            className,
            slidesVisible = '1',
            slidesVisibleMobile = '1',
            showPeek = false,
            showPeekMobile = false,
            slidePadding = '1',
            slidePaddingMobile = '1',
            'data-testid': dataTestId,
            ...props
        },
        ref
    ) => {
        const {orientation} = useCarousel()
        const slidesVisibleLg = (
            showPeek ? slidesVisible + '.25' : slidesVisible
        ) as CarouselSlidesVisibleTypeDesktop
        const slidesVisibleSm = (
            showPeekMobile ? slidesVisibleMobile + '.25' : slidesVisibleMobile
        ) as CarouselSlidesVisibleTypeMobile
        return (
            <div
                ref={ref}
                role="group"
                aria-roledescription="slide"
                className={cn(
                    'carousel-slide',
                    'min-w-0 shrink-0 grow-0 basis-full',
                    orientation === 'horizontal'
                        ? `${paddingClasses[slidePaddingMobile]} ${lgPaddingClasses[slidePadding]}`
                        : 'pt-4',

                    flexBasisClasses[slidesVisibleSm],
                    lgFlexBasisClasses[slidesVisibleLg],
                    className
                )}
                {...props}
                data-testid={dataTestId || 'carousel-Item'}
            />
        )
    }
)

CarouselItem.displayName = 'CarouselItem'

interface CarouselBackButtonProps extends React.PropsWithChildren<typeof Button> {
    buttonIcon?: React.FC<React.SVGProps<SVGSVGElement>>
    className?: string
    'data-testid': string | undefined
}

const CarouselBackButton = React.forwardRef<HTMLButtonElement, CarouselBackButtonProps>(
    (
        {
            className,
            buttonIcon: ButtonIcon = CircleArrowLeft,
            children,
            'data-testid': dataTestId,
            ...props
        },
        ref
    ) => {
        const {scrollPrev, canScrollPrev} = useCarousel()

        return (
            <Button
                ref={ref}
                className={cn('rounded-full flex', className)}
                disabled={!canScrollPrev}
                onClick={scrollPrev}
                asChild
                {...props}
                data-testid={dataTestId || 'carousel-Button'}
            >
                <ButtonIcon
                    className="h-6 w-4 mr-2"
                    data-testid={dataTestId || 'carousel-ButtonIcon'}
                />
                {children}
                <span className="sr-only">Previous slide</span>
            </Button>
        )
    }
)

CarouselBackButton.displayName = 'CarouselBackButton'

interface CarouselButtonProps {
    buttonIcon?: React.FC<React.SVGProps<SVGSVGElement>>
    fillColor?: string
    overlapContent: boolean
    iconAttributes?: Record<string, string>
    className?: string
    'data-testid': string | undefined
}

const CarouselPrevious = React.forwardRef<HTMLButtonElement, CarouselButtonProps>(
    (
        {
            className,
            overlapContent,
            buttonIcon,
            fillColor = 'transparent',
            iconAttributes,
            'data-testid': dataTestId,
            ...props
        },
        ref
    ) => {
        const {orientation, scrollPrev, canScrollPrev} = useCarousel()
        const ButtonIcon = buttonIcon || CircleArrowLeft

        return (
            <Button
                ref={ref}
                className={cn(
                    'absolute z-10 rounded-full mx-auto',
                    'carousel-btn-prev',
                    orientation === 'horizontal'
                        ? `${
                              overlapContent ? 'left-5' : 'lg:-left-10 -left-7'
                          } top-1/2 -translate-y-1/2`
                        : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
                    className
                )}
                disabled={!canScrollPrev}
                onClick={scrollPrev}
                {...props}
                data-testid={dataTestId || 'carousel-Button'}
            >
                <ButtonIcon
                    className="h-6 w-6"
                    fill={fillColor}
                    {...iconAttributes}
                    data-testid={dataTestId || 'carousel-ButtonIcon'}
                />
                <span className="sr-only">Previous slide</span>
            </Button>
        )
    }
)
CarouselPrevious.displayName = 'CarouselPrevious'

const CarouselNext = React.forwardRef<HTMLButtonElement, CarouselButtonProps>(
    (
        {
            className,
            overlapContent,
            buttonIcon,
            fillColor = 'transparent',
            iconAttributes,
            'data-testid': dataTestId,
            ...props
        },
        ref
    ) => {
        const {orientation, scrollNext, canScrollNext} = useCarousel()
        const ButtonIcon = buttonIcon || CircleArrowRight
        return (
            <Button
                ref={ref}
                className={cn(
                    'absolute z-10 rounded-full mx-auto',
                    'carousel-btn-next',
                    orientation === 'horizontal'
                        ? `${
                              overlapContent ? 'right-5' : 'lg:-right-10 -right-7'
                          } top-1/2 -translate-y-1/2`
                        : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
                    className
                )}
                disabled={!canScrollNext}
                onClick={scrollNext}
                {...props}
                data-testid={dataTestId || 'carousel-Button'}
            >
                <ButtonIcon
                    className="h-6 w-6"
                    fill={fillColor}
                    {...iconAttributes}
                    data-testid={dataTestId || 'carousel-ButtonIcon'}
                />
                <span className="sr-only">Next slide</span>
            </Button>
        )
    }
)
CarouselNext.displayName = 'CarouselNext'

interface SlideNavButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    isActive: boolean
    index: number
    buttonColor?: string
    inactiveButtonColor?: string
    buttonClassName?: string
    inactiveButtonClassName?: string
    className?: string
    'data-testid': string | undefined
}

const CircleButton = React.forwardRef<HTMLButtonElement, SlideNavButtonProps>(
    (
        {
            isActive,
            index,
            className,
            buttonColor = 'rgba(0, 0, 0, 1)',
            buttonClassName = '',
            'data-testid': dataTestId,
            ...props
        },
        ref
    ) => {
        return (
            <Button
                ref={ref}
                className={cn(
                    'transition-all rounded-full border-3',
                    isActive
                        ? `w-3 h-3 bg-transparent ${buttonClassName}`
                        : `w-2 h-2 border-transparent `,
                    className
                )}
                style={
                    buttonColor
                        ? {
                              borderColor: isActive ? buttonColor : 'transparent',
                              backgroundColor: isActive ? 'transparent' : buttonColor
                          }
                        : {}
                }
                aria-label={isActive ? 'Active slide indicator' : 'Inactive slide indicator'}
                {...props}
                data-testid={dataTestId || 'carousel-Button'}
            >
                <span className="sr-only">Slide {index + 1}</span>
            </Button>
        )
    }
)
CircleButton.displayName = 'CircleButton'

const FlatButton = React.forwardRef<HTMLButtonElement, SlideNavButtonProps>(
    (
        {
            isActive,
            index,
            className,
            buttonColor = 'hsl(var(--color-mint-02))',
            inactiveButtonColor = 'hsl(var(--color-gray-02))',
            'data-testid': dataTestId,
            ...props
        },
        ref
    ) => {
        return (
            <Button
                ref={ref}
                className={cn('h-1 w-6 mx-1 rounded-full transition-all duration-300', className)}
                style={{backgroundColor: isActive ? buttonColor : inactiveButtonColor}}
                aria-label={isActive ? 'Active slide indicator' : 'Inactive slide indicator'}
                {...props}
                data-testid={dataTestId || 'carousel-Button'}
            >
                <span className="sr-only">Slide {index + 1}</span>
            </Button>
        )
    }
)
FlatButton.displayName = 'FlatButton'

export const navButtonTypes = {
    CircleButton: 'circleButton',
    FlatButton: 'flatButton'
}

const slideNavButtonMap = {
    [navButtonTypes.CircleButton]: CircleButton,
    [navButtonTypes.FlatButton]: FlatButton
}

interface SlideButtonNavigationProps extends React.ButtonHTMLAttributes<HTMLDivElement> {
    buttonType: keyof typeof slideNavButtonMap
    buttonColor?: string
    inactiveButtonColor?: string
    className?: string
    'data-testid': string | undefined
}

const ContentButtonNavigation = React.forwardRef<HTMLDivElement, SlideButtonNavigationProps>(
    (
        {
            className,
            buttonType,
            buttonColor,
            inactiveButtonColor,
            'data-testid': dataTestId,
            ...props
        },
        ref
    ) => {
        const {api} = useCarousel()
        const {selectedIndex, scrollSnaps, onContentNavButtonClick} = useContentNavButton(api)
        const ButtonComponent = slideNavButtonMap[buttonType] || CircleButton

        return (
            <div
                ref={ref}
                className={cn('flex items-center justify-center space-x-2 mt-4', className)}
                {...props}
            >
                {scrollSnaps.map((_, index) => (
                    <ButtonComponent
                        key={index}
                        index={index}
                        onClick={() => onContentNavButtonClick(index)}
                        isActive={index === selectedIndex}
                        {...(buttonColor && {buttonColor})}
                        {...(inactiveButtonColor && {inactiveButtonColor})}
                        data-testid={dataTestId || 'carousel-ButtonComponent'}
                    />
                ))}
            </div>
        )
    }
)

ContentButtonNavigation.displayName = 'ContentButtonNavigation'

export {
    type CarouselApi,
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselBackButton,
    CarouselNext,
    ContentButtonNavigation
}
